import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { hasAnyRole } from "@/lib/roles";
import { useIsMobile } from "@/hooks/useMobile";
import { Search, X, CheckCircle, XCircle, Clock, UserX } from "lucide-react";
import { useState, useEffect, CSSProperties } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";

type BookingStatus = "confirmed" | "cancelled" | "no_show";

export default function GymBookings() {
  const { loading, user } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const isMobile = useIsMobile();
  
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  const [statusFilter, setStatusFilter] = useState<BookingStatus | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [slotFilter, setSlotFilter] = useState<number | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  // Read slotId from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slotId = params.get('slotId');
    if (slotId) {
      setSlotFilter(parseInt(slotId));
    }
  }, []);

  const utils = trpc.useUtils();

  const { data: bookings, isLoading } = trpc.gym.bookings.list.useQuery({
    status: statusFilter === "all" ? undefined : statusFilter
  });

  const cancelMutation = trpc.gym.bookings.adminCancel.useMutation({
    onSuccess: () => {
      toast.success("Buchung storniert");
      utils.gym.bookings.list.invalidate();
      setSelectedBooking(null);
      setCancelReason("");
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const checkInMutation = trpc.gym.bookings.adminCheckIn.useMutation({
    onSuccess: () => {
      toast.success("Check-in erfolgreich");
      utils.gym.bookings.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const noShowMutation = trpc.gym.bookings.markNoShow.useMutation({
    onSuccess: () => {
      toast.success("Als No-Show markiert");
      utils.gym.bookings.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const filteredBookings = bookings?.filter(booking => {
    // Filter by slot if slotFilter is set
    if (slotFilter && booking.slotId !== slotFilter) return false;
    
    // Filter by search query
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      booking.user?.name?.toLowerCase().includes(query) ||
      booking.user?.email?.toLowerCase().includes(query) ||
      booking.slot?.service?.name?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status: BookingStatus, checkedInAt: Date | null) => {
    if (checkedInAt) {
      return <Badge className="bg-green-100 text-green-800 border-green-300">Eingecheckt</Badge>;
    }
    switch (status) {
      case "confirmed":
        return <Badge className="bg-blue-100 text-blue-800 border-blue-300">Bestätigt</Badge>;
      case "cancelled":
        return <Badge className="bg-gray-100 text-gray-800 border-gray-300">Storniert</Badge>;
      case "no_show":
        return <Badge className="bg-red-100 text-red-800 border-red-300">No-Show</Badge>;
    }
  };

  // Auth check
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Laden...</div>;
  }

  if (!user || !hasAnyRole(user.roles, ["admin", "courseadmin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold tracking-tight text-center">
            Anmeldung erforderlich
          </h1>
          <p className="text-sm text-muted-foreground text-center max-w-sm">
            Der Zugriff auf diesen Bereich erfordert Administratorrechte.
          </p>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl(config?.authProvider);
            }}
            size="lg"
            className="w-full"
          >
            Anmelden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}
    >
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <SidebarInset>
        {isMobile && (
          <header className="flex h-14 items-center gap-2 border-b px-4">
            <SidebarTrigger />
          </header>
        )}
        <div className="p-6 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Gym Buchungen</h1>
            <p className="text-muted-foreground">
              Verwalten Sie alle Gym-Buchungen
            </p>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Suche nach Name, E-Mail oder Service..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status filtern" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="confirmed">Bestätigt</SelectItem>
                    <SelectItem value="cancelled">Storniert</SelectItem>
                    <SelectItem value="no_show">No-Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Bookings List */}
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Laden...</div>
          ) : filteredBookings?.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground">Keine Buchungen gefunden</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredBookings?.map(booking => (
                <Card key={booking.id}>
                  <CardContent className="py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-1 h-16 rounded-full" 
                          style={{ backgroundColor: booking.slot?.service?.color ?? "#888" }}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{booking.user?.name || "Unbekannt"}</span>
                            {getStatusBadge(booking.status as BookingStatus, booking.checkedInAt)}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {booking.user?.email}
                          </p>
                          <p className="text-sm mt-1">
                            <span className="font-medium">{booking.slot?.service?.name}</span>
                            {" • "}
                            {booking.slot?.startTime && format(new Date(booking.slot.startTime), "EEEE, d. MMM yyyy, HH:mm", { locale: de })}
                          </p>
                        </div>
                      </div>
                      
                      {booking.status === "confirmed" && !booking.checkedInAt && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => checkInMutation.mutate({ bookingId: booking.id })}
                            disabled={checkInMutation.isPending}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Check-in
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-600 hover:text-orange-700"
                            onClick={() => noShowMutation.mutate({ bookingId: booking.id })}
                            disabled={noShowMutation.isPending}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            No-Show
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setSelectedBooking(booking.id)}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Stornieren
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Cancel Dialog */}
          <Dialog open={selectedBooking !== null} onOpenChange={(open) => {
            if (!open) {
              setSelectedBooking(null);
              setCancelReason("");
            }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Buchung stornieren</DialogTitle>
                <DialogDescription>
                  Möchten Sie diese Buchung wirklich stornieren?
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reason">Grund (optional)</Label>
                  <Input
                    id="reason"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="z.B. Slot wurde abgesagt"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setSelectedBooking(null);
                  setCancelReason("");
                }}>
                  Abbrechen
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    if (selectedBooking) {
                      cancelMutation.mutate({
                        bookingId: selectedBooking,
                        reason: cancelReason || undefined
                      });
                    }
                  }}
                  disabled={cancelMutation.isPending}
                >
                  {cancelMutation.isPending ? "Storniere..." : "Stornieren"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
